/**
 * Post-authentication redirect resolver.
 *
 * The hero quick-action chips on the landing (PR #58) and the kits
 * showcase cards link to /register?intent=<id> so we can route the
 * freshly-signed-up user straight to the surface that matched their
 * intent, instead of dumping them on the generic /dashboard chat.
 *
 * Without this resolver the chips were just marketing signals — the
 * intent param was discarded server-side. With it, the user clicks
 * "Comparer 2 IA" → registers → lands on /dashboard/compare with
 * the context preserved.
 *
 * Usage:
 *   const target = postAuthRedirectFor(searchParams.get("intent"));
 *   navigate(target ?? "/dashboard");
 *
 * Both RegisterPage and LoginPage call this so an existing user
 * arriving via a chip also lands on the right surface.
 */

// Maps an intent id (matches the chip ids in LandingPage.jsx hero +
// kits showcase) to its target dashboard path. Add a row here when
// you add a new chip — the source of truth for "what does this chip do".
const INTENT_TO_PATH = {
  // Hero quick-action chips
  compare: "/dashboard/compare",
  "ask-pdf": "/dashboard/documents",
  pptx: "/dashboard/agents",
  watch: "/dashboard/agents",
  podcast: "/dashboard/podcast",
  // widget intent intentionally absent — that chip routes to
  // /pricing#business directly (Business-tier feature, no free path)

  // Kits showcase cards
  "kit-pm": "/dashboard/kit/pm",
  "kit-marketer": "/dashboard/kit/marketer",
  "kit-founder": "/dashboard/kit/founder",
  "kit-recruiter": "/dashboard/kit/recruiter",
  "kit-teacher": "/dashboard/kit/teacher",
  "kit-freelancer": "/dashboard/kit/freelancer",
};

/**
 * Resolve an intent id to a dashboard path. Returns null when the
 * intent is unknown or empty so the caller can fall back to its
 * default (typically /dashboard).
 */
export function postAuthRedirectFor(intent) {
  if (!intent) return null;
  return INTENT_TO_PATH[intent] ?? null;
}

// Exported for tests and for the next chip-author to know what's
// currently wired without grepping the codebase.
export const KNOWN_INTENTS = Object.freeze(Object.keys(INTENT_TO_PATH));
